import { useState } from 'react';

import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { StatusCodes } from 'http-status-codes';
import PropTypes from 'prop-types';

import { MODAL_NAMES } from '#client/constants/modals';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useModalContext from '#client/hooks/useModalContext';

import useCreateAstraToken from './useCreateAstraToken';
import useCreateInstantFundingDebitCard from './useCreateInstantFundingDebitCard';

// https://docs.astra.finance/reference/post_v1-cards
const astraCardsV1Endpoint = '/v1/cards';

export default function useAstraCardSubmit(handleClose, form) {
  const { openModal } = useModalContext();
  const captureException = useCaptureException();
  const { createAstraToken } = useCreateAstraToken();
  const { createCard } = useCreateInstantFundingDebitCard();
  const { syncAndRefetch } = useAstraProfileContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleResponse = async (status, data) => {
    if (status === StatusCodes.CREATED) {
      const response = await createCard(data.id);
      if (!response.error) {
        await syncAndRefetch();
        openModal({
          name: MODAL_NAMES.INSTANT_DEPOSIT_MODAL,
          checkCardErrorsStep: true
        });
        setTimeout(handleClose, 1000);
        return;
      }
    }
    captureException(
      new Error(
        `Failed to submit debit card, status code: (${status}), data: ${JSON.stringify(data)}`
      ),
      {
        level: 'warning'
      }
    );

    openModal({
      name: MODAL_NAMES.INSTANT_DEPOSIT_MODAL,
      checkCardErrorsStep: true,
      createAstraCardError: 'common error'
    });
    setIsSubmitting(false);
    setTimeout(handleClose, 1000);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const accessToken = await createAstraToken();

      if (!accessToken) {
        throw new Error('Failed to create Astra token');
      }

      form.submit(
        astraCardsV1Endpoint,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        },
        (status, data) => {
          handleResponse(status, data);
        }
      );
    } catch (e) {
      captureException(e, {
        level: 'warning'
      });
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit
  };
}

useAstraCardSubmit.propTypes = {
  handleClose: PropTypes.func.isRequired,
  form: PropTypes.shape({
    submit: PropTypes.func.isRequired
  }).isRequired
};
